import { selectWoundCareTree } from "./../shared/state/selectors/wound-care-tree.selectors";
import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import format from "tz-format";
import { finalize, take, map, tap, publishReplay, refCount, filter } from "rxjs/operators";
import { merge, Observable } from "rxjs";
import clean from "obj-clean";
import { v1 } from "uuid";

import {
	WoundCareActions,
	WoundCareUIActions,
	WoundCareObservationActions,
	WoundCareObservationUIActions,
	PubsubModel,
	ContextService,
} from "../shared";
import {
	ApplicationState,
	WoundCare,
	WoundCareObservation,
	WoundCareStatus,
	CreateWoundCareObservation,
	PatientWoundCareState,
	CreateWoundCare,
} from "../shared/types";
import { WoundCareFormResult } from "./types/wound-care-result";
import { WoundCareObservationFormResult } from "./types/wound-care-observation-result";
import { Action } from "app/shared/types/action";
import { IotModel } from "app/shared/models/iot.model";

@Injectable()
export class WoundCareModel extends PubsubModel {
	private readonly store = inject<Store<ApplicationState>>(Store);
	private readonly iotModel = inject(IotModel);
	private readonly contextService = inject(ContextService);

	private saving = false;
	woundCareTree$ = this.store.select(selectWoundCareTree);
	woundCareLoading$: Observable<boolean> = this.store
		.select((state) => state.ui.woundCare)
		.pipe(map((woundCareUiState) => woundCareUiState.loading));
	woundCareObservationLoading$: Observable<boolean> = this.store
		.select((state) => state.ui.woundCareObservation)
		.pipe(map((woundCareObservationUIState) => woundCareObservationUIState.loading));

	getWoundCaresForPatient(patient: string): Observable<WoundCare[]> {
		return this.store
			.select((state) => state.data.woundCare[patient])
			.pipe(
				filter<PatientWoundCareState>(Boolean),
				map((patientWoundCareState) => patientWoundCareState.woundCare),
				// @ts-expect-error TS2345
				map((woundCares) => this.woundCareMapToArray(woundCares)),
				map((woundCares) => woundCares.filter((woundCare) => woundCare.status === WoundCareStatus.ACTIVE)),
				map((woundCares) => woundCares.sort((a, b) => b.creationDate.localeCompare(a.creationDate))),
			);
	}

	getWoundCareForPatientById(patient: string, id: string): Observable<WoundCare> {
		return this.store
			.select((state) => state.data.woundCare[patient])
			.pipe(
				map((patientWoundCareState) => patientWoundCareState.woundCare),
				// @ts-expect-error TS2345
				map((woundCares) => {
					if (!woundCares || !woundCares.items) {
						return;
					}

					return woundCares.items[id];
				}),
				tap((woundCare) => {
					if (!woundCare) {
						this.loadWoundCares(patient);
					}
				}),
			);
	}

	getWoundCareObservationsForPatientByWoundCareId(patient: string, id: string): Observable<WoundCareObservation[]> {
		// @ts-expect-error TS2322
		return this.store
			.select((state) => state.data.woundCare[patient])
			.pipe(
				map((patientWoundCareState) => patientWoundCareState.observations),
				map((observations) => {
					if (!observations || !observations[id] || !observations[id].items) {
						return;
					}

					return observations[id].items;
				}),
			);
	}

	getWoundCareObservationForPatientByWoundCareIdByIndex(
		patient: string,
		id: string,
		index: number,
	): Observable<WoundCareObservation> {
		return this.getWoundCareObservationsForPatientByWoundCareId(patient, id).pipe(
			map((observations) => observations[index]),
		);
	}

	loadWoundCares(patient: string) {
		this.store
			.pipe(
				take(1),
				map((state) => state.data.woundCare[patient]),
				filter<PatientWoundCareState>(Boolean),
				// @ts-expect-error TS2345
				map((patientWoundCareState) => this.woundCareMapToArray(patientWoundCareState.woundCare)),
				tap((items: WoundCare[]) => {
					if (!items || items.length === 0) {
						this.store.dispatch(WoundCareUIActions.loadingIndicator(true));
					}
				}),
			)
			.subscribe();

		const observable = this.performAsyncAction(WoundCareActions.load(patient)).pipe(
			finalize(() => {
				this.store.dispatch(WoundCareUIActions.loadingIndicator(false));
			}),
			publishReplay(1),
			refCount(),
		);

		observable.subscribe({
			next: (result) => {
				this.store.dispatch(WoundCareActions.set(patient, result.payload.items));
			},
			error: (err) => {
				console.error(err);
			},
		});

		return observable;
	}

	loadWoundCareObservations(patient: string, woundCare: string) {
		this.store
			.select((state) => state.data.woundCare[patient])
			.pipe(
				take(1),

				tap((patientWoundCareState) => {
					if (
						!patientWoundCareState
						|| !patientWoundCareState.observations
						|| !patientWoundCareState.observations[woundCare]
					) {
						this.store.dispatch(WoundCareObservationUIActions.loadingIndicator(true));
					}
				}),
			)
			.subscribe();

		const observable = this.performAsyncAction(WoundCareObservationActions.load(patient, woundCare)).pipe(
			finalize(() => {
				this.store.dispatch(WoundCareObservationUIActions.loadingIndicator(false));
			}),
			publishReplay(1),
			refCount(),
		);

		observable.subscribe({
			next: (result) => {
				this.store.dispatch(WoundCareObservationActions.set(patient, woundCare, result.payload.items));
			},
			error: (err) => {
				console.error(err);
			},
		});

		return observable;
	}

	addWoundCare(patient: string, woundCare: WoundCareFormResult, path: string[]) {
		const date = new Date();
		const dateString = format(date);

		const id = v1().split("-").join("");

		this.addLocal(patient, date, woundCare, path, id);

		this.saving = true;

		// TODO: Remove cast
		const care = clean({
			id,
			path,
			description: woundCare.description,
			creationDate: dateString,
			bodyPart: woundCare.bodyPart,
			pictures: woundCare.pictures.map((picture) =>
				clean({
					filename: picture.filename,
					date: dateString,
					notes: picture.notes,
				}),
			),
			size: woundCare.size,
			origin: woundCare.origin,
			otherOrigin: woundCare.otherOrigin,
			initializeDar: woundCare.initializeDar,
			validationRequired: woundCare.validationRequired,
			darActions: woundCare.darActions,
		}) as CreateWoundCare;

		const observable = this.performAsyncAction(WoundCareActions.create(patient, care)).pipe(
			finalize(() => (this.saving = false)),
			publishReplay(1),
			refCount(),
		);

		observable.subscribe({
			next: () => {
				// Do nothing
			},
		});

		return observable;
	}

	addWoundCareObservation(patient: string, woundCare: string, observationFormResult: WoundCareObservationFormResult) {
		const date = new Date();
		const dateString = format(date);

		this.addLocalObservation(patient, date, woundCare, observationFormResult);

		this.saving = true;

		const { observation } = observationFormResult;

		// TODO: Remove cast
		const newObservation = clean({
			creationDate: dateString,
			important: observationFormResult.important,
			description: observationFormResult.description,
			healed: observation.healed,
			size: observation.healed ? undefined : observation.size,
			pictures: observation.pictures.map((picture) =>
				clean({
					filename: picture.filename,
					date: dateString,
					notes: picture.notes,
				}),
			),
		}) as CreateWoundCareObservation;

		const observable = this.performAsyncAction(
			WoundCareObservationActions.create(patient, woundCare, newObservation),
		).pipe(
			finalize(() => (this.saving = false)),
			publishReplay(1),
			refCount(),
		);

		observable.subscribe({
			next: () => {
				// Do nothing
			},
		});

		return observable;
	}

	// @Override
	listen() {
		return merge(
			this.iotModel.subscribeWithOrganisationPrefix<any>("care/woundcare/healed").pipe(
				tap((payload) => {
					this.loadWoundCareObservations(payload.patient, payload.woundCare);
					this.store.dispatch(WoundCareActions.markAsHealed(payload.patient, payload.woundCare));
				}),
			),
			this.iotModel.subscribeWithOrganisationPrefix<any>("care/woundcare/created").pipe(
				tap((payload) => {
					this.loadWoundCares(payload.patient);
					this.loadWoundCareObservations(payload.patient, payload.woundCare);
				}),
			),
			this.iotModel.subscribeWithOrganisationPrefix<any>("care/woundcare/observations/created").pipe(
				tap((payload) => {
					this.loadWoundCares(payload.patient);
					this.loadWoundCareObservations(payload.patient, payload.woundCare);
				}),
			),
		).subscribe();
	}

	private addLocal(patient: string, date: Date, woundCare: WoundCareFormResult, path: string[], id: string): Action[] {
		const user = this.contextService.context().user;

		const dateString = format(date);

		const observation: WoundCareObservation = {
			size: woundCare.size,
			description: woundCare.description,
			pictures: woundCare.pictures.map((picture) => ({
				filename: picture.filename,
				thumb: picture.filename,
				date: dateString,
				notes: picture.notes,
			})),
			healed: false,
			creationDate: dateString,
			user,
			important: true,
			woundCare: id,
			id: dateString,
		};

		const care: WoundCare = {
			patient,
			id,
			creationDate: dateString,
			origin: woundCare.origin,
			otherOrigin: woundCare.otherOrigin,
			bodyPart: woundCare.bodyPart,
			statusChangedUser: user,
			statusChangedDate: dateString,
			reporter: user,
			// Pictures are set to enpty here since the reducer will set the pictures of the observation on the wound care itself.
			pictures: [],
			category: {
				// TODO: FIX
				id: path[0],
				name: {},
			},
			protocol: {
				// TODO: FIX
				id: "",
				path,
			},
			version: 0,
			status: WoundCareStatus.ACTIVE,
		};

		const addWoundCareAction = WoundCareActions.add(patient, care);
		this.store.dispatch(addWoundCareAction);

		const addWoundCareObservationAction = WoundCareObservationActions.add(patient, care.id, observation);
		this.store.dispatch(addWoundCareObservationAction);

		return [addWoundCareAction, addWoundCareObservationAction];
	}

	private addLocalObservation(
		patient: string,
		date: Date,
		woundCare: string,
		observationFormResult: WoundCareObservationFormResult,
	): Action[] {
		const user = this.contextService.context().user;

		const dateString = format(date);

		const { observation } = observationFormResult;

		const newObservation: WoundCareObservation = {
			description: observationFormResult.description,
			size: observation.size,
			healed: observation.healed,
			pictures: observation.pictures.map((picture) => ({
				filename: picture.filename,
				thumb: picture.filename,
				date: dateString,
				notes: picture.notes,
			})),
			creationDate: dateString,
			user,
			important: true,
			woundCare,
			id: dateString,
		};

		const addWoundCareObservationAction = WoundCareObservationActions.add(patient, woundCare, newObservation);
		this.store.dispatch(addWoundCareObservationAction);

		let markWoundCareAsHealedAction;

		if (observationFormResult.observation.healed) {
			markWoundCareAsHealedAction = WoundCareActions.markAsHealed(patient, woundCare);
			this.store.dispatch(markWoundCareAsHealedAction);
		}

		// @ts-expect-error TS2322
		return [addWoundCareObservationAction, markWoundCareAsHealedAction];
	}

	private woundCareMapToArray(woundCaresMap: { items?: { [key: string]: WoundCare } }): WoundCare[] {
		// @ts-expect-error TS7034
		const res = [];

		if (!woundCaresMap || !woundCaresMap.items) {
			// @ts-expect-error TS7005
			return res;
		}

		for (const key of Object.keys(woundCaresMap.items)) {
			res.push(woundCaresMap.items[key]);
		}

		return res;
	}
}
